import React from "react";
import { useAppDispatch, useAppSelector, useDebounce } from "../../app/hooks";
import { selectLabels } from "../../slices/language";
import IconifyIcon from "../IconifyIcon";
import {
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import "./access-keys-guard-log.css";
import { showError } from "../../utils/helpers";
import api from "../../api";
import { selectUser } from "../../slices/user";
import { AccessKeyCreateResponse } from "../../api/models/access-key-model";
import InvitationCardGuard from "../InvitationCardGuard";
import SearchInput from "../FormElements/SearchInput";
import DesktopDropView from "../DesktopDropView";
import DesktopInvitationDetails from "../DesktopInvitationDetails";
import MobilePagination from "../MobilePagination";

interface AccessKeyGuardLogProps {
  selectedAccessPoint: number | null;
}

const AccessKeysGuardLog = ({
  selectedAccessPoint,
}: AccessKeyGuardLogProps) => {
  const user = useAppSelector(selectUser);
  const labels = useAppSelector(selectLabels);
  const dispatch = useAppDispatch();

  const propertyId = user?.property?.id;

  const [showFilters, setShowFilters] = React.useState<boolean>(false);
  const [selectedInvitationFilter, setSelectedInvitationFilter] =
    React.useState<string>("1");
  const [selectedInvitationType, setSelectedInvitationType] = React.useState<
    "all" | "frequent" | "visitor" | "provider"
  >("all");
  const [selectedInvitationDate, setSelectedInvitationDate] =
    React.useState<string>("all");
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [accessKeys, setAccessKeys] = React.useState<
    AccessKeyCreateResponse[] | null
  >(null);
  const [searchFilter, setSearchFilter] = React.useState<string>("");
  const [showAccessKeyDetails, setShowAccessKeyDetails] =
    React.useState<boolean>(false);
  const [selectedInvitationId, setSelectedInvitationId] = React.useState<
    string | null
  >(null);
  const [selectedInvitation, setSelectedInvitation] =
    React.useState<AccessKeyCreateResponse | null>(null);
  const [page, setPage] = React.useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [totalInvitations, setTotalInvitations] = React.useState<number>(0);

  const debouncedSearchFilter = useDebounce(searchFilter, 300);

  const invitationTypes = [
    {
      value: "all",
      label: labels.all_types,
    },
    {
      value: "visitor",
      label: labels.visitor,
    },
    {
      value: "frequent",
      label: labels.recurrent,
    },
    {
      value: "provider",
      label: labels.provider,
    },
  ];

  const invitationDates = [
    {
      value: "all",
      label: labels.all_dates,
    },
    {
      value: "today",
      label: labels.today,
    },
    {
      value: "last_week",
      label: labels.last_week,
    },
    {
      value: "last_month",
      label: labels.last_month,
    },
    {
      value: "calendar",
      label: labels.calendar,
    },
  ];

  const getAccessKeys = React.useCallback(
    async (debouncedText: string) => {
      if (propertyId && selectedAccessPoint) {
        const typeFilter =
          selectedInvitationType !== "all" ? selectedInvitationType : undefined;
        try {
          setIsLoading(true);
          const response = await api.accessKeysByPropertyRequest(
            propertyId,
            page + 1,
            rowsPerPage,
            typeFilter,
            undefined,
            selectedAccessPoint,
            undefined,
            undefined,
            undefined,
            debouncedText
          );
          if (response) {
            setAccessKeys(response?.data);
            setTotalInvitations(response?.meta?.pagination?.total);
          }
        } catch (error) {
          showError(dispatch, labels.generic_error);
        } finally {
          setIsLoading(false);
        }
      }
    },
    [
      dispatch,
      labels.generic_error,
      page,
      propertyId,
      rowsPerPage,
      selectedAccessPoint,
      selectedInvitationType,
    ]
  );

  React.useEffect(() => {
    getAccessKeys(debouncedSearchFilter);
  }, [debouncedSearchFilter, getAccessKeys]);

  React.useEffect(() => {
    setPage(0);
  }, [
    selectedAccessPoint,
    selectedInvitationType,
    selectedInvitationDate,
    debouncedSearchFilter,
  ]);

  return (
    <div className="w-100 h-100 border-box">
      <div
        className="w-100 border-box flex align-items-center justify-content-between p-4 font-w-500"
        style={{
          height: 50,
          borderBottom: showFilters ? "1px white solid" : "1px #e0e0e0 solid",
          backgroundColor: "white",
          zIndex: 99,
        }}
      >
        <p className="font-16">{labels.access_keys}</p>
        <div className="flex align-items-center justify-content-end gap-4">
          <IconifyIcon
            className="pointer icon-button font-20"
            icon={showFilters ? "mdi:filter-off" : "mdi:filter"}
            onClick={() => setShowFilters(!showFilters)}
          />
          <IconifyIcon
            className="pointer icon-button font-20"
            icon={"zondicons:reload"}
            onClick={() => {
              getAccessKeys(debouncedSearchFilter);
            }}
          />
        </div>
      </div>
      {showFilters && (
        <div
          className="w-100 border-box flex flex-dir-col align-items-center justify-content-between px-4 pb-4 gap-4"
          style={{
            borderBottom: "1px #e0e0e0 solid",
            backgroundColor: "white",
            zIndex: 99,
          }}
        >
          <SearchInput
            value={searchFilter}
            setValue={setSearchFilter}
            size={"small"}
          />
          {/* <FormControl fullWidth>
            <Select
              labelId={"invitation_filters"}
              id={"invitation_filters"}
              value={selectedInvitationFilter}
              onChange={(event: SelectChangeEvent) => {
                setSelectedInvitationFilter(event.target.value as string);
              }}
              size="small"
            >
              {invitationFilters.map((item, index) => (
                <MenuItem key={`${index}-${item?.value}`} value={item?.value}>
                  {item?.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl> */}
          <FormControl fullWidth>
            <Select
              labelId={"selected_inv_filter"}
              id={"selected_inv_filter"}
              value={
                selectedInvitationFilter === "1"
                  ? selectedInvitationType
                  : selectedInvitationDate
              }
              onChange={(event: SelectChangeEvent) => {
                if (selectedInvitationFilter === "1") {
                  const value = event.target.value as string;
                  setSelectedInvitationType(
                    value as "provider" | "all" | "frequent" | "visitor"
                  );
                } else {
                  setSelectedInvitationDate(event.target.value as string);
                }
              }}
              size="small"
            >
              {selectedInvitationFilter === "1" &&
                invitationTypes.map((item, index) => (
                  <MenuItem key={`${index}-${item?.value}`} value={item?.value}>
                    {item?.label}
                  </MenuItem>
                ))}
              {selectedInvitationFilter === "2" &&
                invitationDates.map((item, index) => (
                  <MenuItem key={`${index}-${item?.value}`} value={item?.value}>
                    {item?.label}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </div>
      )}
      {isLoading ? (
        <div
          className={`px-4 flex justify-content-center align-items-center ${
            showFilters ? "height-filters" : "height-no-filters"
          }`}
        >
          <CircularProgress />
        </div>
      ) : (
        <div
          className={`px-4 ${
            showFilters ? "height-filters" : "height-no-filters"
          }`}
          style={{ paddingBottom: 37 }}
        >
          {accessKeys && accessKeys?.length > 0 ? (
            <>
              {accessKeys?.map((item, index) => (
                <div
                  key={`${item?.id}-${index}`}
                  className={
                    accessKeys.length - 1 === index ? "mt-4 mb-4" : "mt-4"
                  }
                  onClick={() => {
                    setSelectedInvitationId(
                      item?.verifiable_ids[0]?.documentId,
                    );
                    setSelectedInvitation(item);
                    setShowAccessKeyDetails(true);
                  }}
                >
                  <InvitationCardGuard
                    selectedAccessKey={item}
                    onPress={() => {}}
                  />
                </div>
              ))}
            </>
          ) : (
            <>
              <p className="text-center mt-10 title-color">
                {labels.no_results_found}
              </p>
            </>
          )}
        </div>
      )}
      <div
        style={{
          position: "sticky",
          bottom: 0,
          backgroundColor: "white",
          zIndex: 99,
          borderTop: "1px #e0e0e0 solid",
        }}
      >
        <MobilePagination
          page={page}
          rowsPerPage={rowsPerPage}
          total={totalInvitations}
          onChangePage={(p) => setPage(p)}
        />
      </div>
      <DesktopDropView
        showView={showAccessKeyDetails}
        setShowView={setShowAccessKeyDetails}
        content={
          <div className="flex h-100 border-box" style={{ width: 500 }}>
            <div className="w-100 h-100 border-box p-8 scroll">
              <DesktopInvitationDetails invitationId={selectedInvitationId} />
            </div>
          </div>
        }
        extraFunc={selectedInvitation ? true : false}
        title={labels.invitation_details}
      />
    </div>
  );
};

export default AccessKeysGuardLog;
